import { call, put, takeLatest } from "redux-saga/effects";
import axios from "axios";
import { toast } from "react-toastify";

import {
  saveTicketRequest,
  saveTicketSuccess,
  saveTicketFailure,
  getTicketsRequest,
  getTicketsSuccess,
  getTicketsFailure,
  updateTicketStatusRequest,
  updateTicketStatusSuccess,
  updateTicketStatusFailure,
  getTicketStatusHistoryRequest,
  getTicketStatusHistorySuccess,
  getTicketStatusHistoryFailure,
  assignTicketRequest,
  assignTicketSuccess,
  assignTicketFailure,
  saveCommentRequest,
  saveCommentSuccess,
  saveCommentFailure,
  getCommentsRequest,
  getCommentsSuccess,
  getCommentsFailure,
  fetchInstalaxmiAdminRequest,
  fetchInstalaxmiAdminSuccess,
  fetchInstalaxmiAdminFailure,
} from "./ticketSlice";

function* handleSaveTicket(action) {
  try {
    const formData = new FormData();

    Object.keys(action.payload).forEach((key) => {
      formData.append(key, action.payload[key]);
    });

    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/saveTicket`,
        formData,
      ),
    );

    if (response.data.success) {
      yield put(saveTicketSuccess(response.data));

      if (action.payload?.id) {
        toast.success(response.data.message || "Ticket updated successfully");
      } else {
        toast.success(response.data.message || "Ticket created successfully");
      }

      yield put(getTicketsRequest({ page: 1, limit: 10 }));
    } else {
      yield put(saveTicketFailure(response.data.message));
      toast.error(response.data.message || "Failed to save ticket");
    }
  } catch (error) {
    yield put(saveTicketFailure(error.message));
    toast.error(error.response?.data?.message || "Failed to save ticket");
  }
}

function* fetchTickets(action) {
  try {
    const payload = action.payload || {};

    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/getTickets`,
        {
          params: payload,
        },
      ),
    );

    yield put(getTicketsSuccess(response.data));
  } catch (error) {
    yield put(getTicketsFailure(error.message));
    toast.error("Failed to fetch tickets");
  }
}

function* updateTicketStatusSaga(action) {
  try {
    const { id, status } = action.payload;

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/updateTicketStatus/${id}`,
      { status },
    );

    if (response.data.success) {
      yield put(updateTicketStatusSuccess(response.data.data));
      toast.success(response.data.message || "Status updated successfully");

      yield put(getTicketsRequest({ page: 1, limit: 10 }));
    } else {
      yield put(updateTicketStatusFailure(response.data.message));
      toast.error(response.data.message || "Failed to update status");
    }
  } catch (error) {
    yield put(updateTicketStatusFailure(error.message));
    toast.error(error.response?.data?.message || "Failed to update status");
  }
}

function* getTicketStatusHistorySaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/ticket-status-history/${action.payload}`,
      ),
    );

    yield put(getTicketStatusHistorySuccess(response.data));
  } catch (error) {
    yield put(getTicketStatusHistoryFailure(error.message));
    toast.error("Failed to fetch ticket status history");
  }
}

function* assignTicketSaga(action) {
  try {
    const { ticketId, assignee, dueDate } = action.payload;

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/assignTicket/${ticketId}`,
      {
        assignedTo: assignee,
        dueDate: dueDate,
      },
    );

    if (response.data.success) {
      yield put(assignTicketSuccess(response.data.data));
      toast.success(response.data.message || "Ticket assigned successfully");
      yield put(getTicketsRequest({ page: 1, limit: 10 }));
    } else {
      yield put(assignTicketFailure(response.data.message));
      toast.error(response.data.message || "Failed to assign ticket");
    }
  } catch (error) {
    yield put(assignTicketFailure(error.message));
    toast.error(error.response?.data?.message || "Failed to assign ticket");
  }
}

function* getCommentsSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/getComments/${action.payload}`,
      ),
    );

    yield put(getCommentsSuccess(response.data));
  } catch (error) {
    yield put(getCommentsFailure(error.message));
    toast.error("Failed to fetch comments");
  }
}

function* saveCommentSaga(action) {
  try {
    const formData = new FormData();

    Object.keys(action.payload).forEach((key) => {
      formData.append(key, action.payload[key]);
    });

    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/tickets/saveComment`,
        formData,
      ),
    );

    if (response.data.success) {
      yield put(saveCommentSuccess(response.data));
      toast.success(response.data.message || "Comment added successfully");

      if (action.payload.ticketId) {
        yield put(getCommentsRequest(action.payload.ticketId));
      }
    } else {
      yield put(saveCommentFailure(response.data.message));
      toast.error(response.data.message || "Failed to add comment");
    }
  } catch (error) {
    yield put(saveCommentFailure(error.message));
    toast.error(error.response?.data?.message || "Failed to add comment");
  }
}

function* loadAssignableUsers(url, method) {
  const response = yield call(axios[method], url);
  const users = response?.data?.data;
  return Array.isArray(users) ? users : [];
}

function* fetchInstalaxmiAdmin() {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    let users = [];
    try {
      users = yield call(
        loadAssignableUsers,
        `${base}/api/v1/tickets/getInstalaxmiAdmin`,
        "post",
      );
    } catch (error) {
      users = [];
    }

    if (!users.length) {
      users = yield call(
        loadAssignableUsers,
        `${base}/api/v1/users/getInstalaxmiUser`,
        "get",
      );
    }

    const formattedUsers = users.map((u) => ({
      _id: u._id,
      name: u.name || u.phone || "Unnamed",
    }));

    yield put(fetchInstalaxmiAdminSuccess(formattedUsers));
  } catch (error) {
    yield put(
      fetchInstalaxmiAdminFailure({
        message: error.response?.data?.message || error.message,
        type: "error",
      }),
    );
  }
}

export function* ticketSaga() {
  yield takeLatest(saveTicketRequest.type, handleSaveTicket);
  yield takeLatest(getTicketsRequest.type, fetchTickets);
  yield takeLatest(updateTicketStatusRequest.type, updateTicketStatusSaga);
  yield takeLatest(
    getTicketStatusHistoryRequest.type,
    getTicketStatusHistorySaga,
  );
  yield takeLatest(assignTicketRequest.type, assignTicketSaga);
  yield takeLatest(getCommentsRequest.type, getCommentsSaga);
  yield takeLatest(saveCommentRequest.type, saveCommentSaga);
  yield takeLatest(fetchInstalaxmiAdminRequest.type, fetchInstalaxmiAdmin);
}
